import { useMemo, useState } from "react";
import { Check, Copy, Download } from "lucide-react";

/**
 * Shows a JSON value (a Try it response, a History before/after copy) without letting it take
 * over the page: a one-line summary, a table for lists of records, and JSON in which long lists
 * are cut to their first items. The panel scrolls on its own; Copy and Download always use the
 * full value.
 */

const PREVIEW_ITEMS = 20;
const TABLE_PAGE = 25;
const MAX_COLUMNS = 8;
const COLUMN_SAMPLE = 20;

// Highlighting turns every token into an element; past this size plain text keeps the page responsive.
const HIGHLIGHT_LIMIT = 200 * 1024;
// One match per JSON token: a key (string then colon), a string, a number, or a literal.
const JSON_TOKEN = /("(?:\\.|[^"\\])*")(\s*:)?|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|\btrue\b|\bfalse\b|\bnull\b/g;
// Postman's light theme colours, each a different hue and weight so they read apart.
const TOKEN_CLASS = {
  key: "text-rose-800",
  string: "text-blue-700",
  number: "text-emerald-700",
  literal: "font-semibold text-violet-700"
};

const isRecord = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const isScalar = (value) => value === null || typeof value !== "object";

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The list a person would call "the results": the body itself, or its longest top-level list. */
function findMainList(body) {
  if (Array.isArray(body)) return { path: null, items: body };
  if (!isRecord(body)) return null;
  const lists = Object.entries(body).filter(([, v]) => Array.isArray(v) && v.length > 0);
  if (lists.length === 0) return null;
  const [path, items] = lists.reduce((a, b) => (b[1].length > a[1].length ? b : a));
  return { path, items };
}

/** Columns for a table: the scalar keys the first records share, in the order they appear. */
function pickColumns(items) {
  const columns = [];
  items.slice(0, COLUMN_SAMPLE).forEach((item) => {
    Object.entries(item).forEach(([key, value]) => {
      if (isScalar(value) && !columns.includes(key)) columns.push(key);
    });
  });
  return columns.slice(0, MAX_COLUMNS);
}

/** A copy of `value` with every list longer than PREVIEW_ITEMS cut short and marked. */
function trimLists(value) {
  if (Array.isArray(value)) {
    const kept = value.slice(0, PREVIEW_ITEMS).map(trimLists);
    const hidden = value.length - kept.length;
    return hidden > 0 ? [...kept, `… ${hidden.toLocaleString()} more items`] : kept;
  }
  if (isRecord(value)) return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, trimLists(v)]));
  return value;
}

function describe(list, bytes) {
  const size = formatBytes(bytes);
  if (!list) return size;
  const count = `${list.items.length.toLocaleString()} item${list.items.length === 1 ? "" : "s"}`;
  return list.path ? `${count} in “${list.path}” · ${size}` : `${count} · ${size}`;
}

/** The text of a non-JSON answer, which the engine wraps as {"_raw": "..."}; else null. */
function rawText(body) {
  return isRecord(body) && typeof body._raw === "string" && Object.keys(body).length === 1 ? body._raw : null;
}

function tokenKind(match, isKey) {
  if (isKey) return "key";
  if (match.startsWith('"')) return "string";
  return /^-?\d/.test(match) ? "number" : "literal";
}

/** JSON text with keys, strings, numbers and literals coloured, as API clients show a body. */
export function JsonCode({ text }) {
  const parts = useMemo(() => {
    if (text.length > HIGHLIGHT_LIMIT) return [text];
    const out = [];
    let last = 0;
    for (const m of text.matchAll(JSON_TOKEN)) {
      if (m.index > last) out.push(text.slice(last, m.index));
      const token = m[2] ? m[1] : m[0];
      out.push(<span key={m.index} className={TOKEN_CLASS[tokenKind(token, !!m[2])]}>{token}</span>);
      if (m[2]) out.push(m[2]);
      last = m.index + m[0].length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
  }, [text]);
  return parts;
}

const CODE_PANEL = "max-h-96 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-4 font-mono text-xs leading-relaxed text-gray-500";

function download(text, name, raw) {
  const url = URL.createObjectURL(new Blob([text], { type: raw ? "text/plain" : "application/json" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `${(name || "response").replace(/[^\w.-]+/g, "-").toLowerCase()}.${raw ? "txt" : "json"}`;
  link.click();
  // Revoked later: some browsers start the download only after click() returns.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Tab({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-2.5 py-1 text-xs font-medium ${active ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
    >
      {children}
    </button>
  );
}

function ToolButton({ onClick, label, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
    >
      {children}
    </button>
  );
}

function RecordTable({ items }) {
  const [shown, setShown] = useState(TABLE_PAGE);
  const columns = useMemo(() => pickColumns(items), [items]);
  return (
    <>
      <div className="max-h-96 overflow-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-left text-xs">
          <thead className="sticky top-0 bg-gray-50 text-gray-500">
            <tr>
              <th className="px-3 py-2 font-medium">#</th>
              {columns.map((c) => (
                <th key={c} className="px-3 py-2 font-medium">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.slice(0, shown).map((item, i) => (
              // Rows have no stable id of their own; the list never reorders while shown.
              <tr key={i} className="text-gray-900">
                <td className="px-3 py-1.5 text-gray-400">{i + 1}</td>
                {columns.map((c) => (
                  <td key={c} className="max-w-xs truncate px-3 py-1.5 font-mono">
                    {item[c] === undefined ? "" : String(item[c])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
        <span>
          Showing {Math.min(shown, items.length).toLocaleString()} of {items.length.toLocaleString()}
        </span>
        {shown < items.length && (
          <button type="button" onClick={() => setShown((n) => n + TABLE_PAGE)} className="font-medium text-blue-700 hover:underline">
            Show {Math.min(TABLE_PAGE, items.length - shown)} more
          </button>
        )}
      </div>
    </>
  );
}

function JsonView({ body, full }) {
  const [showAll, setShowAll] = useState(false);
  const preview = useMemo(() => JSON.stringify(trimLists(body), null, 2), [body]);
  const trimmed = preview !== full;
  return (
    <>
      <pre className={CODE_PANEL}>
        <JsonCode text={showAll ? full : preview} />
      </pre>
      {trimmed && (
        <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
          <span>{showAll ? "Showing the full response." : `Long lists show their first ${PREVIEW_ITEMS} items.`}</span>
          <button type="button" onClick={() => setShowAll((v) => !v)} className="font-medium text-blue-700 hover:underline">
            {showAll ? "Show less" : "Show full response"}
          </button>
        </div>
      )}
    </>
  );
}

/** Text answers (HTML error pages, plain text) wrap inside the panel: they can hold very long unbroken strings. */
function RawView({ text }) {
  return (
    <pre className={`${CODE_PANEL} whitespace-pre-wrap break-words text-gray-800 [overflow-wrap:anywhere]`}>
      {text}
    </pre>
  );
}

export default function ResponseViewer({ body, devMode, name, allowTable = true }) {
  const raw = rawText(body);
  const full = useMemo(() => raw ?? JSON.stringify(body, null, 2) ?? "", [body, raw]);
  const list = useMemo(() => (allowTable ? findMainList(body) : null), [body, allowTable]);
  const tableable = !!list && list.items.some(isRecord);
  const [view, setView] = useState(tableable && !devMode ? "table" : "json");
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const bytes = new Blob([full]).size;
  const showTable = tableable && view === "table";
  return (
    <div className="min-w-0">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500">{raw === null ? describe(list, bytes) : `Text, not JSON · ${formatBytes(bytes)}`}</p>
        <div className="flex items-center gap-2">
          {tableable && (
            <div className="flex rounded-lg bg-gray-100 p-0.5">
              <Tab active={showTable} onClick={() => setView("table")}>Table</Tab>
              <Tab active={!showTable} onClick={() => setView("json")}>JSON</Tab>
            </div>
          )}
          <ToolButton onClick={copy} label="Copy the full response">
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy"}
          </ToolButton>
          <ToolButton onClick={() => download(full, name, raw !== null)} label="Download the full response">
            <Download className="h-3.5 w-3.5" />
            Download
          </ToolButton>
        </div>
      </div>
      {raw !== null ? (
        <RawView text={raw} />
      ) : showTable ? (
        <RecordTable items={list.items.filter(isRecord)} />
      ) : (
        <JsonView body={body} full={full} />
      )}
    </div>
  );
}
